// ── CHANGE ORDERS TAB ─────────────────────────────────────────────────────
// Mounted as a sub-tab inside InvoicesTab. Lists every Change Order, with
// inline-expand to show the field_changes detail (before/after) and Approve /
// Reject buttons for the owner on pending COs.
//
// Pending COs do NOT modify items until the owner approves. Approval applies
// the field_changes via context.approveChangeOrder and flips status to
// 'approved'. (The planned QuickBooks push was dropped 2026-10-17;
// impacting COs.

import React, { useState, useMemo } from 'react';
import { useAppContext } from './AppContext';
import { isBillingImpactingField, calcChangeOrderDelta } from '../utils';

// ── STYLES ─────────────────────────────────────────────────────────────────
var S = {
  wrap:        { fontFamily: "'DM Sans', sans-serif", fontSize: 12, color: '#111111', background: '#FFFFFF' },
  toolbar:     { display: 'flex', alignItems: 'center', height: 44, padding: '0 14px', gap: 8, borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5', flexWrap: 'wrap' },
  pageTitle:   { fontSize: 13, fontWeight: 500, color: '#111111' },
  vsep:        { width: '0.5px', height: 18, background: '#D8D7D2', margin: '0 2px' },
  th:          { padding: '8px 14px', textAlign: 'left', fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', whiteSpace: 'nowrap', borderBottom: '0.5px solid #D8D7D2', background: '#F7F7F5' },
  td:          { padding: '10px 14px', fontSize: 12, color: '#111111', borderBottom: '0.5px solid #EFEFEC', verticalAlign: 'top' },
  tbtnGhost:   { height: 26, padding: '0 10px', borderRadius: 5, border: '0.5px solid #D8D7D2', background: '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#111111', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnPrimary: { height: 26, padding: '0 11px', borderRadius: 5, border: '0.5px solid #1A5C38', background: '#1A5C38', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#FFFFFF', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  tbtnDanger:  { height: 26, padding: '0 10px', borderRadius: 5, border: '0.5px solid #C8322A', background: '#FFFFFF', cursor: 'pointer', fontSize: 11, fontWeight: 500, color: '#C8322A', fontFamily: 'inherit', whiteSpace: 'nowrap' },
  detailRow:   { padding: '12px 14px', background: '#FAFAF8', borderBottom: '0.5px solid #EFEFEC' },
};

var STATUS_STYLE = {
  pending:     { dot: '#F5C800', text: '#8A6900', label: 'Pending' },
  approved:    { dot: '#1A5C38', text: '#1A5C38', label: 'Approved' },
  rejected:    { dot: '#888880', text: '#888880', label: 'Rejected' },
  pushed:      { dot: '#1A5C38', text: '#1A5C38', label: 'Approved (legacy sync)' },
  push_failed: { dot: '#1A5C38', text: '#1A5C38', label: 'Approved (legacy sync)' },
  void:        { dot: '#888880', text: '#888880', label: 'Void' },
};

// ── HELPERS ────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var d = new Date(iso);
    if (isNaN(d.getTime())) return String(iso).slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch (e) { return String(iso).slice(0, 10); }
}

function truncate(s, n) {
  s = String(s || '');
  return s.length > n ? s.slice(0, n - 1) + '…' : s;
}

// Render a signed dollar delta. Used in the CO list row so the owner can
// see the dollar amount without expanding. Pending COs show a live preview
// (calcChangeOrderDelta against current items state); approved COs show
// the stored delta_amount. Zero deltas render as "—" (no $ change).
function fmtDelta(n) {
  if (n == null) return '—';
  var v = Number(n);
  if (isNaN(v) || v === 0) return '—';
  var sign = v > 0 ? '+' : '−';
  var abs = Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return sign + '$' + abs;
}

function StatusPill(p) {
  var s = STATUS_STYLE[p.status] || STATUS_STYLE.pending;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
      <span style={{ width: 8, height: 8, borderRadius: '50%', background: s.dot }} />
      <span style={{ fontSize: 11, color: s.text, fontWeight: 500 }}>{s.label}</span>
    </span>
  );
}

// ── MAIN COMPONENT ─────────────────────────────────────────────────────────
export default function ChangeOrdersTab() {
  var ctx = useAppContext();
  var changeOrders = ctx.changeOrders || [];
  var items = ctx.items || [];
  var approveChangeOrder = ctx.approveChangeOrder;
  var rejectChangeOrder = ctx.rejectChangeOrder;
  var isOwner = ctx.isOwner;

  var es = useState(null);    var expandedId = es[0];   var setExpandedId = es[1];
  var ws = useState(null);    var working = ws[0];      var setWorking = ws[1];
  var msgs = useState('');    var msg = msgs[0];        var setMsg = msgs[1];
  var fs = useState('all');   var statusFilter = fs[0]; var setStatusFilter = fs[1];

  var filtered = useMemo(function() {
    if (statusFilter === 'all') return changeOrders;
    return changeOrders.filter(function(co) { return co.status === statusFilter; });
  }, [changeOrders, statusFilter]);

  var counts = useMemo(function() {
    var c = { all: changeOrders.length, pending: 0, approved: 0, rejected: 0 };
    changeOrders.forEach(function(co) { if (c[co.status] != null) c[co.status]++; });
    return c;
  }, [changeOrders]);

  // Two approval outcomes — see migration 20260505_change_orders_accounting_impact:
  //   accountingImpact=false → spec-only: field changes apply, audit trail
  //                            captured, not added to client billing.
  //   accountingImpact=true  → accounting: same as above PLUS delta_amount
  //                            rolls into the next final invoice (or fires
  //                            as a Credit Memo / additional Invoice draw if
  //                            final has already shipped). Phase 5 work.
  async function handleApprove(coId, accountingImpact) {
    setMsg('');
    setWorking(coId);
    var result = await approveChangeOrder(coId, accountingImpact);
    setWorking(null);
    if (!result || !result.ok) {
      var e = result && result.error;
      setMsg('Approve failed: ' + (typeof e === 'string' ? e : (e && e.message) || 'unknown error'));
    }
  }

  async function handleReject(coId) {
    var reason = window.prompt('Reason for rejection (optional):', '');
    if (reason === null) return; // user cancelled the prompt
    setMsg('');
    setWorking(coId);
    var result = await rejectChangeOrder(coId, reason || '');
    setWorking(null);
    if (!result || !result.ok) {
      var e = result && result.error;
      setMsg('Reject failed: ' + (typeof e === 'string' ? e : (e && e.message) || 'unknown error'));
    }
  }

  return (
    <div style={S.wrap}>
      <div style={S.toolbar}>
        <span style={S.pageTitle}>Change Orders</span>
        <span style={{ fontSize: 11, color: '#888880' }}>· {filtered.length}</span>
        <div style={S.vsep} />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['all','pending','approved','rejected'].map(function(k) {
            var active = statusFilter === k;
            return (
              <button key={k}
                onClick={function() { setStatusFilter(k); }}
                style={{
                  height: 26, padding: '0 10px', borderRadius: 13,
                  border: '0.5px solid ' + (active ? '#111111' : '#D8D7D2'),
                  background: active ? '#111111' : '#FFFFFF',
                  color: active ? '#FFFFFF' : '#111111',
                  fontSize: 11, fontWeight: 500, fontFamily: 'inherit',
                  cursor: 'pointer', whiteSpace: 'nowrap',
                }}>
                {k.charAt(0).toUpperCase() + k.slice(1)} ({counts[k] || 0})
              </button>
            );
          })}
        </div>
      </div>

      {msg && (
        <div style={{ padding: '9px 14px', background: '#FCEAE8', border: '0.5px solid #C8322A', color: '#A1271F', fontSize: 11 }}>
          {msg}
        </div>
      )}

      {filtered.length === 0 ? (
        <div style={{ padding: '40px 14px', textAlign: 'center', color: '#888880', fontSize: 12 }}>
          No change orders {statusFilter !== 'all' ? 'in this status' : 'yet'}.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={S.th}>CO Ref</th>
              <th style={S.th}>Project</th>
              <th style={S.th}>Filed</th>
              <th style={S.th}>Reason</th>
              <th style={S.th}>Items</th>
              <th style={Object.assign({}, S.th, { textAlign: 'right' })}>$ Delta</th>
              <th style={S.th}>Status</th>
              <th style={S.th}></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(function(co) {
              var expanded = expandedId === co.id;
              var fieldChanges = co.fieldChanges || [];
              var uniqueItemIds = {};
              fieldChanges.forEach(function(c) { uniqueItemIds[c.itemId] = true; });
              var itemCount = Object.keys(uniqueItemIds).length;
              var canManage = isOwner && co.status === 'pending';
              // Pending COs preview the delta against current items state;
              // approved COs read the stored value (frozen at approval).
              // Rejected COs show "—" since they were never applied.
              var deltaToShow = co.status === 'rejected'
                ? null
                : (co.status === 'pending' ? calcChangeOrderDelta(co, items) : (co.deltaAmount || 0));
              var deltaColor = deltaToShow == null || deltaToShow === 0
                ? '#888880'
                : (deltaToShow > 0 ? '#1A5C38' : '#C8322A');
              return (
                <React.Fragment key={co.id}>
                  <tr
                    onClick={function() { setExpandedId(expanded ? null : co.id); }}
                    style={{ cursor: 'pointer', background: expanded ? '#F7F7F5' : 'transparent' }}>
                    <td style={Object.assign({}, S.td, { fontWeight: 500 })}>{co.coRef || '—'}</td>
                    <td style={S.td}>{co.projectLabel || '—'}</td>
                    <td style={S.td}>{fmtDate(co.filedAt)}</td>
                    <td style={S.td}>{truncate(co.reason, 60)}</td>
                    <td style={S.td}>{itemCount}</td>
                    <td style={Object.assign({}, S.td, { textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: deltaColor, fontWeight: deltaToShow ? 500 : 400 })}>
                      {fmtDelta(deltaToShow)}
                    </td>
                    <td style={S.td}>
                      <StatusPill status={co.status} />
                      {co.status === 'approved' && co.accountingImpact === true && (
                        <span style={{ marginLeft: 6, display: 'inline-flex', fontSize: 9, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#1A5C38', background: '#FFFFFF', border: '0.5px solid #1A5C38', borderRadius: 10, padding: '2px 8px' }}
                          title="Approved as accounting change — delta will be billed to the client.">
                          Accounting
                        </span>
                      )}
                      {co.status === 'approved' && co.accountingImpact === false && (
                        <span style={{ marginLeft: 6, display: 'inline-flex', fontSize: 9, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#888880', background: '#FFFFFF', border: '0.5px solid #D8D7D2', borderRadius: 10, padding: '2px 8px' }}
                          title="Approved as spec only — no client billing.">
                          Spec only
                        </span>
                      )}
                    </td>
                    <td style={Object.assign({}, S.td, { textAlign: 'right', whiteSpace: 'nowrap' })}>
                      <span style={{ color: '#888880', fontSize: 11 }}>{expanded ? '▾' : '▸'}</span>
                    </td>
                  </tr>
                  {expanded && (
                    <tr>
                      <td colSpan={8} style={{ padding: 0 }}>
                        <div style={S.detailRow}>
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 4 }}>Reason</div>
                            <div style={{ fontSize: 12, color: '#111111', whiteSpace: 'pre-wrap' }}>{co.reason || '—'}</div>
                          </div>

                          <div style={{ display: 'flex', gap: 24, marginBottom: 12, fontSize: 11, color: '#555552' }}>
                            <div>Filed by <strong style={{ color: '#111111' }}>{co.filedBy || '—'}</strong> on {fmtDate(co.filedAt)}</div>
                            {co.approvedAt && <div>Approved by <strong style={{ color: '#111111' }}>{co.approvedBy || '—'}</strong> on {fmtDate(co.approvedAt)}</div>}
                            {co.rejectedAt && <div>Rejected by <strong style={{ color: '#111111' }}>{co.rejectedBy || '—'}</strong> on {fmtDate(co.rejectedAt)}{co.rejectedReason ? ' — ' + co.rejectedReason : ''}</div>}
                          </div>

                          <div style={{ fontSize: 10, fontWeight: 500, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#888880', marginBottom: 6 }}>
                            Field Changes ({fieldChanges.length})
                          </div>
                          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#FFFFFF', border: '0.5px solid #E0E0DC', borderRadius: 4 }}>
                            <thead>
                              <tr>
                                <th style={Object.assign({}, S.th, { background: '#F4F4F0' })}>Item</th>
                                <th style={Object.assign({}, S.th, { background: '#F4F4F0' })}>Field</th>
                                <th style={Object.assign({}, S.th, { background: '#F4F4F0' })}>Before</th>
                                <th style={Object.assign({}, S.th, { background: '#F4F4F0' })}>After</th>
                                <th style={Object.assign({}, S.th, { background: '#F4F4F0' })}></th>
                              </tr>
                            </thead>
                            <tbody>
                              {fieldChanges.map(function(c, idx) {
                                return (
                                  <tr key={idx}>
                                    <td style={S.td}>
                                      {c.itemNumber ? <span style={{ color: '#888880' }}>{c.itemNumber} · </span> : null}
                                      {c.itemName || c.itemId}
                                    </td>
                                    <td style={S.td}>{c.fieldLabel || c.field}</td>
                                    <td style={Object.assign({}, S.td, { color: '#888880' })}>{c.before || '—'}</td>
                                    <td style={Object.assign({}, S.td, { fontWeight: 500 })}>{c.after || '—'}</td>
                                    <td style={S.td}>
                                      {(c.impactsBilling || isBillingImpactingField(c.field)) && (
                                        <span style={{ display: 'inline-flex', fontSize: 9, fontWeight: 500, letterSpacing: '0.05em', textTransform: 'uppercase', color: '#3730A3', background: '#E0E7FF', borderRadius: 10, padding: '2px 8px' }}>$ Impact</span>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>

                          {canManage && (
                            <div style={{ marginTop: 14, display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                              <button
                                onClick={function(e) { e.stopPropagation(); handleReject(co.id); }}
                                disabled={working === co.id}
                                style={S.tbtnDanger}>
                                Reject
                              </button>
                              <button
                                onClick={function(e) { e.stopPropagation(); handleApprove(co.id, false); }}
                                disabled={working === co.id}
                                style={Object.assign({}, S.tbtnGhost || S.tbtnPrimary, working === co.id ? { opacity: 0.6, cursor: 'wait' } : {})}
                                title="Apply the changes to the item. Audit trail only — does not bill the client.">
                                {working === co.id ? 'Working…' : 'Approve — Spec only'}
                              </button>
                              <button
                                onClick={function(e) { e.stopPropagation(); handleApprove(co.id, true); }}
                                disabled={working === co.id}
                                style={Object.assign({}, S.tbtnPrimary, working === co.id ? { opacity: 0.6, cursor: 'wait' } : {})}
                                title="Apply the changes AND roll the dollar delta into the next final invoice.">
                                {working === co.id ? 'Working…' : 'Approve — Accounting'}
                              </button>
                            </div>
                          )}
                          {!isOwner && co.status === 'pending' && (
                            <div style={{ marginTop: 14, fontSize: 11, color: '#888880', textAlign: 'right' }}>
                              Pending owner approval.
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
